import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import styled, { css } from 'styled-components';
import Link from 'next/link';

import { GqlProductDemandCount } from 'types/graphql';

import usePaths from 'src/hooks/use-paths';
import useCart from 'src/hooks/use-cart';
import useUI from 'src/hooks/use-ui';

import CartItemClass from 'src/state/cart-item';

import { isWeightedProduct } from 'shared/helpers/products';
import { SkeletonLoader } from 'shared/components';
import { formatCurrency } from 'shared/helpers/utils';
import { groupOptionsWithPrices } from 'src/utils/helpers/product';
import TrashCanIcon from 'src/assets/trash-can-icon';
import ProductImage from 'src/components/product-image';

import { useCartItemDiscount } from './use-cart-item-discount';
import { useDiscountBadgeText } from './use-discount-badge-text';
import { DiscountBadge } from './discount-badge';
import { ProductDemand } from './product-demand';
import { Quantity } from './quantity';
import { WeightDropdown } from './weight-dropdown';

type CartItemProps = {
  item: CartItemClass;
  shouldShowDiscountBadge?: boolean;
  shouldShowRemoveButton?: boolean;
  shouldShowDemandCount?: boolean;
  shouldShowWeightDropdown?: boolean;
  shouldShowTopBorder?: boolean;
  quantityPrefix?: string | undefined;
  onProductDemandChange?: (productId: string, productDemandCount: GqlProductDemandCount | undefined) => void;
};

export const CartItem = observer(
  ({
    item,
    shouldShowDiscountBadge = false,
    shouldShowRemoveButton = false,
    shouldShowWeightDropdown = false,
    shouldShowDemandCount = false,
    shouldShowTopBorder = false,
    quantityPrefix,
    onProductDemandChange,
  }: CartItemProps): JSX.Element => {
    const { closeCartDrawer, isKiosk } = useUI();
    const { dispensary, isCalculating, menuType, removeItem } = useCart();
    const { product, option } = item;
    const { brand, name, strainType } = product;
    const { href } = usePaths({ product, dispensary });
    const weight = option || 'N/A';
    const isWeighted = isWeightedProduct(product);
    const weightOptions = groupOptionsWithPrices({ product, menuType, isKiosk });
    const selectedWeightOption = weightOptions.find((weightOption) => weightOption.value === weight);
    const weightLabel = isWeighted && selectedWeightOption?.label;
    const showWeightDropdown = shouldShowWeightDropdown && selectedWeightOption && weightOptions.length > 1;

    const [quantityError, setQuantityError] = useState('');

    const info = [brand?.name, strainType, item.additionalOption].filter(Boolean);

    const {
      originalPrice,
      bogoDiscountedPrice,
      adjustedPrice,
      hideDiscounts,
      isWeightOnSale,
      hasDiscount,
      showOriginalPrice,
      showDiscountedPrice,
    } = useCartItemDiscount(item);

    const { discountBadgeText, type } = useDiscountBadgeText({
      originalPrice,
      bogoDiscountedPrice,
      discountedPrice: adjustedPrice,
      item,
      shouldHideDiscounts: hideDiscounts,
      isWeightOnSale,
    });

    return (
      <Wrapper data-testid='cart-item-container' $shouldShowTopBorder={shouldShowTopBorder}>
        <Link href={href}>
          <ImageWrapper href={href} onClick={closeCartDrawer}>
            <ProductImage product={product} width={92} height={92} alt={name} data-testid='product-image' />
          </ImageWrapper>
        </Link>

        <Content>
          <Link href={href}>
            <Anchor href={href} onClick={closeCartDrawer}>
              {shouldShowDiscountBadge && discountBadgeText && type && (
                <DiscountBadge badgeText={discountBadgeText} type={type} />
              )}

              <Name>{name}</Name>

              {(info.length > 0 || weightLabel) && (
                <Info>
                  {info.map((infoItem) => (
                    <div key={infoItem}>{infoItem}</div>
                  ))}

                  {weightLabel &&
                    (showWeightDropdown ? (
                      <WeightDropdown
                        item={item}
                        selectedWeight={selectedWeightOption.value}
                        weightOptions={weightOptions}
                      />
                    ) : (
                      <div>{weightLabel}</div>
                    ))}
                </Info>
              )}
            </Anchor>
          </Link>

          {!discountBadgeText && (
            <ProductDemand
              item={item}
              shouldShowDemandCount={shouldShowDemandCount}
              onProductDemandChange={onProductDemandChange}
            />
          )}

          <Actions>
            <Quantity item={item} setQuantityError={setQuantityError} quantityPrefix={quantityPrefix} />

            {shouldShowRemoveButton && (
              <RemoveButton data-testid='remove-cart-item' onClick={() => removeItem(item.key)}>
                <TrashCanIcon height={16} width={16} />
                Remove
              </RemoveButton>
            )}

            <Price>
              {isCalculating ? (
                <SkeletonLoader data-testid='skeleton-loader' height={18} width={56} useExactDimensions />
              ) : (
                <div>
                  {showDiscountedPrice && (
                    <DiscountedPrice>{formatCurrency(bogoDiscountedPrice ?? adjustedPrice)}</DiscountedPrice>
                  )}
                  {showOriginalPrice && (
                    <OriginalPrice hasDiscount={hasDiscount}>{formatCurrency(originalPrice)}</OriginalPrice>
                  )}
                </div>
              )}
            </Price>
          </Actions>

          {quantityError && <QuantityError>{quantityError}</QuantityError>}
        </Content>
      </Wrapper>
    );
  }
);

const Wrapper = styled.div`
  background: #fff;
  display: flex;
  gap: 12px;
  padding: 20px 16px;
  width: 100%;

  ${({ $shouldShowTopBorder }) =>
    $shouldShowTopBorder &&
    css`
      border-top: 2px solid #f0f0f0;
    `}
`;

const ImageWrapper = styled.a`
  display: block;
  flex: 0 0 auto;
`;

const Content = styled.div`
  flex: 1 1 auto;
`;

const Anchor = styled.a`
  display: block;
`;

const Name = styled.div`
  color: #121516;
  display: block;
  font-size: 14px;
  font-weight: 600;
  line-height: ${20 / 14};
`;

const Info = styled.div`
  color: #646d72;
  display: flex;
  font-size: 12px;
  line-height: ${16 / 12};
  margin-top: 4px;

  > div:not(:last-child) {
    margin-right: 8px;
    padding-right: 9px;
    position: relative;

    ::after {
      background: #d2d7da;
      bottom: 2px;
      content: '';
      display: block;
      position: absolute;
      right: 0;
      top: 2px;
      width: 1px;
    }
  }
`;

const Actions = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
  margin-top: 16px;
`;

const RemoveButton = styled.button`
  align-items: center;
  appearance: none;
  background: none;
  border: none;
  color: #646d72;
  cursor: pointer;
  display: flex;
  font-size: 12px;
  gap: 2px;
  line-height: 1;
`;

const Price = styled.div`
  display: flex;
  flex: 1 1 auto;
  justify-content: flex-end;
  text-align: right;
`;

const activePriceStyles = css`
  color: #000;
  font-size: 16px;
  font-weight: 600;
  line-height: ${18 / 16};
`;

const DiscountedPrice = styled.div`
  ${activePriceStyles};
`;

const OriginalPrice = styled.div<{ hasDiscount: boolean }>`
  ${({ hasDiscount }) =>
    hasDiscount
      ? css`
          color: #646d72;
          font-size: 12px;
          line-height: 1;
          margin-top: 2px;
          text-decoration: line-through;
        `
      : activePriceStyles};
`;

const QuantityError = styled.div`
  color: ${({ theme }) => theme.colors.red[45]};
  font-size: 12px;
  line-height: 1;
  margin-top: 8px;
`;
